import Vue from "vue"
import { extend, setInteractionMode, ValidationObserver, ValidationProvider } from "vee-validate/dist/vee-validate.full"
import { size } from "vee-validate/dist/rules"
import pL from "js-regex-pl"

setInteractionMode("passive")

/**
 * Read a file's pixel dimensions. Resolves `null` when it cannot be decoded, so callers can tell
 * "unreadable file" apart from "readable but wrong size". Revokes the object URL on both paths.
 */
const readImageSize = function (file) {
  const URL = window.URL || window.webkitURL

  return new Promise(function (resolve) {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    const finish = function (result) {
      URL.revokeObjectURL(objectUrl)
      resolve(result)
    }

    image.onerror = function () {
      finish(null)
    }

    image.onload = function () {
      finish({ width: this.width, height: this.height })
    }

    image.src = objectUrl
  })
}

const UNREADABLE_IMAGE = "This file could not be read as an image. Try re-saving it as JPG or PNG."

/** 1% margin so a 2:1-in-intent image off by a pixel (2501x1250) is not rejected as a rounding artefact. */
const ASPECT_TOLERANCE = 0.01

extend("fileLimit", {
  validate(value, args) {
    if (value.length <= parseInt(args.fileLimit)) {
      return true
    }
    return "You have exceeded the maximum number of tracks allowed for your plan."
  },
  params: ["fileLimit"],
  message: "Tracks limit exceeded.",
})

extend("aspectSquare", {
  validate(value) {
    return readImageSize(value[0]).then((size) => !!size && size.width === size.height)
  },
  params: ["aspectSquare"],
  message: "Image must have equal width and height.",
})

/**
 * Landscape range check for images centre-cropped to several ratios downstream. A range rather than
 * one exact ratio: the damaging case is a square or portrait upload, while anything already
 * landscape stays usable. Returning a string uses it as the message, as `fileLimit` does above.
 */
extend("aspectRatio", {
  validate(value, { min, max }) {
    const lower = Number(min)
    const upper = Number(max)

    return readImageSize(value[0]).then((size) => {
      if (!size) return UNREADABLE_IMAGE

      const ratio = size.width / size.height
      const required = `Image aspect ratio must be between ${lower}:1 and ${upper}:1.`
      const actual = `This one is ${ratio.toFixed(2)}:1 (${size.width}x${size.height}px)`

      // Too tall and too wide fail for opposite reasons; one message is wrong for one of them.
      if (ratio < lower * (1 - ASPECT_TOLERANCE)) {
        return `${required} ${actual}, which is too tall — most of the picture would be cut off on wide screens.`
      }

      if (ratio > upper * (1 + ASPECT_TOLERANCE)) {
        return `${required} ${actual}, which is too wide — the sides would be cut off on narrow screens.`
      }

      return true
    })
  },
  params: ["min", "max"],
  message: "Invalid image aspect ratio.",
})

extend("dimensions", {
  validate(value, args) {
    const [minWidth, minHeight] = args.min.split("x").map(Number)
    const [maxWidth, maxHeight] = args.max ? args.max.split("x").map(Number) : []

    return readImageSize(value[0])
      .then((size) => {
        if (!size) return UNREADABLE_IMAGE

        const actual = `${size.width}x${size.height}px`

        if (size.width < minWidth || size.height < minHeight) {
          return `Image must be at least ${args.min}px — this one is ${actual}.`
        }

        if (args.max && (size.width > maxWidth || size.height > maxHeight)) {
          return `Image must be no larger than ${args.max}px — this one is ${actual}.`
        }

        return true
      })
      .catch(() => false)
  },
  params: ["min", "max"],
  message: "Invalid image size.",
})

extend("url", {
  validate: (str) => {
    let pattern = new RegExp(
      "^(https?:\\/\\/)?" + // protocol
      "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
      "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
      "(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*" + // port and path
      "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
      "(\\#[-a-z\\d_]*)?$",
      "i"
    ) // fragment locator
    return !!pattern.test(str)
  },
  message: "The {_field_} field must be a valid URL",
})

extend("pretty_url", {
  validate: (value) => {
    let strongRegex = new RegExp("^([A-Za-z0-9\\-\\_]+)$")
    return strongRegex.test(value)
  },
  message: "The {_field_} should be at least 6 characters. Only letters, numbers, dashes and underscores are allowed.",
})

extend("welcome_prompt_size", {
  ...size,
  message: "The {_field_} field size must be less than 10MB",
})

extend("station_name", {
  validate: (value) => {
    let strongRegex = new RegExp(`^([${pL}0-9\\.\\-\\_\\(\\) ]+)$`)
    return strongRegex.test(value)
  },
  message:
    "The station name should be at least 6 characters. Only letters, numbers, spaces, dashes, dots and underscores are allowed.",
})

extend("zone_offset", {
  validate: (value) => {
    if (!value) return true
    const zoneOffsetPattern = /^[+-]\d{2}:\d{2}$/
    return zoneOffsetPattern.test(value)
  },
  message: 'Zone offset must be in format "+HH:mm" or "-HH:mm" (e.g., "+05:00" or "-03:00")',
})

extend("multiselect", {
  validate: (value) => {
    if (!value.path) return false
    return true
  },
  message: "This field is required",
})

extend("relay_url", {
  validate(value) {
    const regex = /^(https?:\/\/|rtmp:\/\/)[^\s/$.?#].[^\s]*$/i;
    return regex.test(value);
  },
  message: "The {_field_} field must be a valid HTTP(S) or RTMP URL"
});

extend("stream_url", {
  validate(value) {
    if (!value) return true;
    const regex = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
    return regex.test(value);
  },
  message: "The {_field_} field must be a valid HTTP or HTTPS URL"
});

extend("password_has_digit", {
  validate: (value) => /\d/.test(value),
  message: "The {_field_} must contain at least one digit",
})

extend("password_has_special", {
  validate: (value) => /[^A-Za-z0-9]/.test(value),
  message: "The {_field_} must contain at least one special character",
})

export const resolveJsonError = (value) => {
  try {
    JSON.parse(value)
    return null
  } catch (e) {
    const posMatch = e.message.match(/at position (\d+)/)
    if (posMatch) {
      const pos = parseInt(posMatch[1], 10)
      const line = value.slice(0, pos).split("\n").length
      return `Invalid JSON at line ${line}: ${e.message}`
    }
    return `Invalid JSON: ${e.message}`
  }
}

extend("json", {
  validate: (value) => {
    const error = resolveJsonError(value)
    if (!error) return true
    return { valid: false, data: { jsonError: error } }
  },
  message: (_, { jsonError }) => jsonError,
})

extend("after_date", {
  params: ["start"],
  validate: (value, { start }) => {
    if (!value || !start) return true
    return Number(value) > Number(start)
  },
  message: "The {_field_} must be after the start date",
})

extend("max_array_item_length", {
  validate: (value, { length }) => {
    if (!Array.isArray(value)) return true
    return value.every((item) => String(item).length <= Number(length))
  },
  params: ["length"],
  message: "Each {_field_} entry must be {length} characters or fewer",
})

extend("password_not_email", {
  params: ["email"],
  validate: (value, { email }) => {
    if (!email) return true
    return value.toLowerCase() !== email.toLowerCase()
  },
  message: "The {_field_} must not be the same as your email",
})

Vue.component("ValidationObserver", ValidationObserver)
Vue.component("ValidationProvider", ValidationProvider)
